// Shared timing for the layout transitions in the left panel. Blocks there
// appear and disappear while the rest of the column stays put, so they animate
// their *box* (height/margin/padding), not just opacity — otherwise the
// siblings snap into the freed space the moment the node leaves the DOM.
export const MOTION_DURATION = 500
export const MOTION_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'

// Users who asked their OS for less motion jump straight to the end state.
export const motionDuration = () => {
  const reduced =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  return reduced ? 0 : MOTION_DURATION
}

// Tracks the in-flight height animation per element so a second call can cancel
// the first without losing the element's original (class-driven) overflow.
const running = new WeakMap<HTMLElement, { animation: Animation; overflow: string }>()

// Animate an element between two explicit heights, clipping its overflow while
// it runs. Use when a content swap changes a box's natural height and the
// siblings below it would otherwise jump.
export const animateHeight = (el: HTMLElement, from: number, to: number) => {
  if (!from || !to || Math.abs(to - from) < 2) return

  const previous = running.get(el)
  previous?.animation.cancel()
  const overflow = previous ? previous.overflow : el.style.overflow

  el.style.overflow = 'hidden'
  const animation = el.animate([{ height: `${from}px` }, { height: `${to}px` }], {
    duration: motionDuration(),
    easing: MOTION_EASING,
  })
  running.set(el, { animation, overflow })

  const restore = () => {
    // A newer animation already took over — it owns the overflow now.
    if (running.get(el)?.animation !== animation) return
    running.delete(el)
    el.style.overflow = overflow
  }
  animation.onfinish = restore
  animation.oncancel = restore
}
